import { fontFamilies } from '@rh/shared/constants/fonts'
import { zColor } from '@/utils/zod'
import { z } from 'zod'

// Agency Details Schema
export const agencyDetailsSchema = z.object({
  showAddressLine1: z.boolean(),
  addressLine1: z.string().min(1).max(255).or(z.undefined()),
  showAddressLine2: z.boolean(),
  addressLine2: z.string().min(1).max(255).or(z.undefined()),
  showAddressLine3: z.boolean(),
  addressLine3: z.string().min(1).max(255).or(z.undefined()),
  showPhoneNumber: z.boolean(),
  phoneNumber: z.string().min(1).max(255).or(z.undefined()),
  showUrl: z.boolean(),
  url: z.string().or(z.undefined()),
  showEmailAddress: z.boolean(),
  emailAddress: z.string().or(z.undefined()),
  showLinkedIn: z.boolean(),
  linkedIn: z.string().or(z.undefined()),
})

// Recruiter Details Schema
export const recruiterDetailsSchema = z.object({
  showName: z.boolean(),
  name: z.string().min(1).max(255).or(z.undefined()),
  showPhoneNumber: z.boolean(),
  phoneNumber: z.string().min(1).max(255).or(z.undefined()),
  showEmailAddress: z.boolean(),
  emailAddress: z.string().or(z.undefined()),
  showLinkedIn: z.boolean(),
  linkedIn: z.string().or(z.undefined()),
})

// Shape Schema
export const shapeSchema = z.enum([
  'columns-1-image',
  'columns-1-text',
  'columns-2',
  'columns-3',
])

// Header/Footer Schema
export const headerFooterSchema = z.object({
  shape: shapeSchema,
  showAgencyLogo: z.boolean(),
  showAgencyName: z.boolean(),
  agencyName: z.string().min(1).max(255).or(z.undefined()),
  agencyDetails: agencyDetailsSchema,
  recruiterDetails: recruiterDetailsSchema,
})

// Watermark Schema
export const watermarkSchema = z.object({
  text: z.string().min(1).max(255),
  color: zColor,
  transparency: z.number().min(0).max(100),
})

// Main Form Schema
export const customizeFormSchema = z.object({
  // Theme
  textColor: zColor,
  borderColor: zColor,
  fontFamily: z.enum(fontFamilies),

  // Header
  includeHeader: z.boolean(),
  header: headerFooterSchema,

  // Footer
  includeFooter: z.boolean(),
  footer: headerFooterSchema,

  // Watermark
  includeWatermark: z.boolean(),
  watermark: watermarkSchema,
})

export type CustomizeFormValues = z.infer<typeof customizeFormSchema>

// Initial Form Values
export const initialCustomizeFormValues: CustomizeFormValues = {
  textColor: '#000000',
  borderColor: '#000000',
  fontFamily: 'Inter',
  includeHeader: true,
  header: {
    shape: 'columns-2',
    showAgencyLogo: true,
    showAgencyName: true,
    agencyName: '',
    agencyDetails: {
      showAddressLine1: true,
      addressLine1: undefined,
      showAddressLine2: true,
      addressLine2: undefined,
      showAddressLine3: true,
      addressLine3: undefined,
      showPhoneNumber: true,
      phoneNumber: undefined,
      showUrl: true,
      url: undefined,
      showEmailAddress: true,
      emailAddress: undefined,
      showLinkedIn: true,
      linkedIn: undefined,
    },
    recruiterDetails: {
      showName: true,
      name: undefined,
      showPhoneNumber: true,
      phoneNumber: undefined,
      showEmailAddress: true,
      emailAddress: undefined,
      showLinkedIn: true,
      linkedIn: undefined,
    },
  },
  includeFooter: false,
  footer: {
    shape: 'columns-1-image',
    showAgencyLogo: true,
    showAgencyName: true,
    agencyName: '',
    agencyDetails: {
      showAddressLine1: true,
      addressLine1: undefined,
      showAddressLine2: true,
      addressLine2: undefined,
      showAddressLine3: true,
      addressLine3: undefined,
      showPhoneNumber: true,
      phoneNumber: undefined,
      showUrl: true,
      url: undefined,
      showEmailAddress: true,
      emailAddress: undefined,
      showLinkedIn: true,
      linkedIn: undefined,
    },
    recruiterDetails: {
      showName: true,
      name: undefined,
      showPhoneNumber: true,
      phoneNumber: undefined,
      showEmailAddress: true,
      emailAddress: undefined,
      showLinkedIn: true,
      linkedIn: undefined,
    },
  },
  includeWatermark: true,
  watermark: {
    text: 'CONFIDENTIAL',
    color: '#FF0000',
    transparency: 34,
  },
}
